import React, { useState } from "react";
import { projects } from "../data/mock";
import { ExternalLink, Clock, User, Tag } from "lucide-react";

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  
  const categories = ["All", ...new Set(projects.map(project => project.category))];
  
  const filteredProjects = selectedCategory === "All" 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  // Category-specific gradient colors (professional, brand-aligned)
  const getCategoryGradient = (category) => {
    const gradients = {
      "Brand Strategy & E-Commerce": "from-orange-500 via-red-500 to-pink-600", // Fiyah colors
      "Content Strategy & Thought Leadership": "from-purple-600 via-indigo-600 to-blue-600", // Deep wisdom
      "Infrastructure Transformation": "from-slate-600 via-gray-700 to-zinc-800", // Technical/solid
      "Network Services": "from-blue-600 via-cyan-600 to-teal-600", // Connectivity
      "Digital Transformation": "from-emerald-600 via-teal-600 to-cyan-600", // Innovation/growth
      "Brand & Content Strategy": "from-amber-500 via-yellow-500 to-orange-500", // Creative/bright
    };
    
    return gradients[category] || "from-gray-600 via-gray-700 to-gray-800"; // Default
  };

  // Category icon/symbol
  const getCategorySymbol = (category) => {
    const symbols = {
      "Brand Strategy & E-Commerce": "🔥",
      "Content Strategy & Thought Leadership": "✨",
      "Infrastructure Transformation": "⚡",
      "Network Services": "🌐",
      "Digital Transformation": "🚀",
      "Brand & Content Strategy": "💡",
    };
    
    return symbols[category] || "◆";
  };

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="text-center mb-16">
        <h2 className="text-5xl lg:text-6xl font-light tracking-tight text-black mb-6">
          Projects
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto font-light leading-relaxed">
          Real-world projects demonstrating strategic thinking, technical execution, 
          and measurable business impact across digital transformation initiatives.
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-6 py-3 rounded-lg text-sm font-light tracking-wide transition-all duration-300 ${
              selectedCategory === category
                ? "bg-black text-white shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-black"
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Projects Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {filteredProjects.map((project, index) => (
          <div 
            key={project.id}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group"
          >
            {/* Project Visual - Custom Images */}
            <div className={`aspect-[4/3] bg-black relative overflow-hidden`}>
              {/* Custom Images for Each Project by Category */}
              {project.category === "Spiritual Brand Development" && (
                <img 
                  src="https://customer-assets.emergentagent.com/job_aligned-kotf/artifacts/ac0no4i4_2.png"
                  alt="INSPIYAH CULTURE - Spiritual Brand Development"
                  className="w-full h-full object-cover"
                />
              )}
              {project.category === "Content Strategy & Thought Leadership" && (
                <img 
                  src="https://customer-assets.emergentagent.com/job_aligned-kotf/artifacts/pj3xjc20_3.png"
                  alt="INSPIYAH MAIL - Content Strategy"
                  className="w-full h-full object-cover"
                />
              )}
              {project.category === "Infrastructure Transformation" && (
                <img 
                  src="https://customer-assets.emergentagent.com/job_aligned-kotf/artifacts/whfe97iu_4.png"
                  alt="Infrastructure Transformation"
                  className="w-full h-full object-cover"
                />
              )}
              {project.category === "Voice & Network Services" && (
                <img 
                  src="https://customer-assets.emergentagent.com/job_aligned-kotf/artifacts/tk51wmbv_5.png"
                  alt="Voice & Network Services"
                  className="w-full h-full object-cover"
                />
              )}
              {project.category === "Digital Transformation" && (
                <img 
                  src="https://customer-assets.emergentagent.com/job_aligned-kotf/artifacts/f40rpezj_6.png"
                  alt="Digital Transformation"
                  className="w-full h-full object-cover"
                />
              )}
              {project.category === "Brand & Content Strategy" && (
                <img 
                  src="https://customer-assets.emergentagent.com/job_aligned-kotf/artifacts/je478qm8_7.png"
                  alt="Brand & Content Strategy"
                  className="w-full h-full object-cover"
                />
              )}
              {!["Spiritual Brand Development", "Content Strategy & Thought Leadership", "Infrastructure Transformation", "Voice & Network Services", "Digital Transformation", "Brand & Content Strategy"].includes(project.category) && (
                /* Fallback to gradient for any other projects */
                <>
                  <div className={`absolute inset-0 bg-gradient-to-br ${getCategoryGradient(project.category)}`} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-8xl opacity-20 group-hover:scale-110 group-hover:opacity-30 transition-all duration-500">
                      {getCategorySymbol(project.category)}
                    </div>
                  </div>
                </>
              )}
              
              {/* Decorative Pattern Overlay */}
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 80%, white 1px, transparent 1px)',
                backgroundSize: '50px 50px'
              }} />
              
              {/* Category Badge */}
              <div className="absolute top-4 left-4">
                <div className="bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                  <span className="text-xs font-medium tracking-wide text-gray-800">
                    {project.category}
                  </span>
                </div>
              </div>

              {/* Hover Overlay - Lighter for better contrast */}
              <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-all duration-300" />

              {/* Store Access Button for INSPIYAH CULTURE */}
              {project.title === "INSPIYAH CULTURE" && (
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button 
                    onClick={() => window.location.href = '/store'}
                    className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-black px-6 py-3 rounded-full font-medium tracking-wide hover:scale-105 transition-transform duration-300 shadow-2xl flex items-center space-x-2"
                  >
                    <span>Visit Store</span>
                    <ExternalLink size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-lg font-light text-black mb-2 tracking-wide group-hover:text-gray-700 transition-colors">
                  {project.title}
                </h3>
                <p className="text-gray-600 font-light leading-relaxed text-sm line-clamp-3">
                  {project.description}
                </p>
              </div>

              {/* Project Meta */}
              <div className="flex flex-col gap-2 mb-4 text-xs text-gray-500 border-t border-gray-100 pt-4">
                <div className="flex items-center space-x-2">
                  <Clock size={14} />
                  <span className="font-light">{project.duration}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <User size={14} />
                  <span className="font-light">{project.role}</span>
                </div>
              </div>

              {/* Top 4 Technologies */}
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {project.technologies.slice(0, 4).map((tech, techIndex) => (
                    <span 
                      key={techIndex}
                      className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-light rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                  {project.technologies.length > 4 && (
                    <span className="px-2 py-1 bg-gray-50 text-gray-400 text-xs font-light rounded-full">
                      +{project.technologies.length - 4}
                    </span>
                  )}
                </div>
              </div>

              {/* Top 2 Key Outcomes */}
              <div className="border-t border-gray-100 pt-4">
                <ul className="space-y-2 mb-2">
                  {project.outcomes.slice(0, 2).map((outcome, outcomeIndex) => (
                    <li 
                      key={outcomeIndex}
                      className="flex items-start space-x-2 text-xs text-gray-600 font-light"
                    >
                      <div className="w-1.5 h-1.5 bg-black rounded-full mt-1.5 flex-shrink-0" />
                      <span className="line-clamp-2">{outcome}</span>
                    </li>
                  ))}
                </ul>
                
                {project.outcomes.length > 2 && (
                  <button className="text-xs text-gray-400 font-light hover:text-black transition-colors flex items-center space-x-1 group/btn">
                    <span>+{project.outcomes.length - 2} more outcomes</span>
                    <ExternalLink size={10} className="group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Project Statistics */}
      <div className="mt-20 pt-16 border-t border-gray-200">
        <div className="text-center mb-12">
          <h3 className="text-3xl font-light text-black mb-4 tracking-wide">
            Project Impact
          </h3>
          <p className="text-gray-600 font-light">
            Measurable results across digital transformation initiatives
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center group hover:-translate-y-2 transition-transform duration-300">
            <div className="text-4xl font-light text-black mb-2 group-hover:scale-110 transition-transform">40%</div>
            <div className="text-sm text-gray-600 font-light">Efficiency Improvement</div>
          </div>
          <div className="text-center group hover:-translate-y-2 transition-transform duration-300">
            <div className="text-4xl font-light text-black mb-2 group-hover:scale-110 transition-transform">98%</div>
            <div className="text-sm text-gray-600 font-light">On-Time Delivery</div>
          </div>
          <div className="text-center group hover:-translate-y-2 transition-transform duration-300">
            <div className="text-4xl font-light text-black mb-2 group-hover:scale-110 transition-transform">60%</div>
            <div className="text-sm text-gray-600 font-light">Engagement Increase</div>
          </div>
          <div className="text-center group hover:-translate-y-2 transition-transform duration-300">
            <div className="text-4xl font-light text-black mb-2 group-hover:scale-110 transition-transform">30%</div>
            <div className="text-sm text-gray-600 font-light">Time Reduction</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;